/**
 * Booking Confirmation Page
 *
 * Displays booking confirmation after successful payment and booking creation.
 *
 * Route: /booking/confirmed/:bookingId?tenant-handle=<handle>&club-id=<id>
 *
 * Data Flow:
 * - Priority 1: Use booking data passed via navigation state (fast, no API call)
 * - Priority 2: If missing (page refresh), fetch booking by ID (requires auth)
 * - Fetch payment method details separately for displaying last 4 digits
 *
 * Auth Requirements:
 * - Protected route (requires authentication)
 * - Who may see it is the API's answer: the booker or a participant (`ownedByUser`).
 *   Total Paid is the booker's amount, so it shows to the booker only.
 *
 * @module pages/booking
 */

import { useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router";
import { useAuth } from "~/services/auth";
import { formatClubDate, formatClubTime, asUtcDate, asTimezone } from "@butter/library/datetime";
import { useBookingQuery } from "~/services/internalApi/booking/hooks";
import { bookingService } from "~/services/internalApi/booking";
import { ExtendBookingRow } from "./components";
import { AccessCodesCard } from "@butter/ui/domains/access";
import { usePaymentMethodQuery } from "~/features/payment/hooks";
import { deriveDeferredBalance, describePaymentMethod, deriveInvoiceTotalsRows, formatInvoiceMoney } from "@butter/ui/domains/billing";
import {
  TicketCard,
  TicketDivider,
} from "@butter/ui/patterns";
import { LessonPageHeader } from "../lessons/components";
import { CopyButton } from "@butter/ui/primitives";
import { DownloadIcon } from "@butter/ui/icons";
import { BayImageCard } from "@butter/ui/domains/booking";
import { CheckmarkIcon } from "@butter/ui/icons";
// Never react-router's Link/useNavigate: this app carries tenant-handle + club-id in the query
// string, and a bare path drops them — MissingParamsGuard then blocks the whole app.
import { TenantLink } from "~/lib/navigation";
import { safeToNumericUserId } from "~/lib/utils/userIdUtils";
import type { ApiBookingWithRelations, ApiAccessControlResult, ApiBookingAccessCodes } from "~/services/internalApi/booking/api/types";
import { clearAllBookingData } from "~/services/internalApi/booking/context/sessionStorage";
import { forgetPromoCode, BOOKING_PROMO_KEY } from "~/features/promotions";
import { useHashModal } from "~/hooks/useHashModal";
import { useSimulatorCompanionEnabled } from "~/features/simulatorCompanion";
import { butterToast } from "@butter/ui/patterns";
import { formatFeeRate } from "@butter/library/pricing";
import { useInvoiceQuery } from "~/services/internalApi/invoice";
import { formatLessonHours } from "~/services/internalApi/lessons";
import { lessonPackName } from "@butter/library/lessons";
import { SubscriptionUsagePanel, type UsageLimit } from "@butter/ui/domains/subscriptions";
import { useLessonInstructor } from "../lessons/useLessonInstructor";
import { lessonUsageRows } from "../lessons/lessonPackDisplay";
import { lessonCoverage } from "@butter/library/lessons";

/**
 * Pricing information for booking purchase
 */
interface BookingPricing {
  subtotal: number;
  tax: number;
  total: number;
  taxRate: number;
  /** Guest fees charged for additional players. Absent when there were none. */
  guestFee?: number;
}

/**
 * Navigation state type
 */
interface BookingConfirmationState {
  booking?: ApiBookingWithRelations;
  /** Access-control result from the createBooking response — the door PIN is `accessControl.accessCode`. */
  accessControl?: ApiAccessControlResult;
  /** The createBooking response's Access Codes block (simulator-companion D77). */
  access?: ApiBookingAccessCodes | null;
  /** The invoice the booking was charged on (createBooking response); `null` when nothing was charged. */
  invoiceId?: number | null;
  paymentMethodId?: string;
  pricing?: BookingPricing; // Future: Pricing breakdown from purchase flow
  /**
   * `variant="lesson"` only. Set by `LessonPaymentStep` to the just-resolved
   * name, so it's usually present. Absent on a hard refresh — the render
   * below then re-resolves from `booking.staffId` (QA MUST-FIX) the same way
   * `LessonPaymentStep` does, and omits the identity only if that also misses.
   */
  instructorName?: string;
}

/**
 * Booking Confirmation Page Component
 */
export interface BookingConfirmationPageProps {
  /**
   * `demo` (mounted at /demo/confirmed/:bookingId) restyles the copy — demo
   * heading, no Total Paid row (a "$0.00" line on a free demo is noise), and
   * "Book Another Demo" pointing back at /demo. Everything else — nav-state-first
   * data, auth guards, the access-code pill — is shared verbatim.
   *
   * `lesson` (mounted at /lessons/confirmed/:bookingId) adds the instructor
   * identity, gates Total Paid on `booking.paymentAmount > 0` rather than on the
   * variant alone (a fully-covered lesson has genuinely paid $0.00), and
   * repeats to "Book Another Lesson" → /lessons/book. Everything else is
   * shared verbatim, including the access-code pill.
   */
  variant?: 'standard' | 'demo' | 'lesson';
}

export function BookingConfirmationPage({ variant = 'standard' }: BookingConfirmationPageProps = {}) {
  const isDemo = variant === 'demo';
  const isLesson = variant === 'lesson';
  const rowSize = isLesson ? 'lg' : 'sm';
  const location = useLocation();
  const { bookingId: bookingIdParam } = useParams<{ bookingId: string }>();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  // Extract booking ID from URL params
  const bookingIdNum = bookingIdParam ? parseInt(bookingIdParam, 10) : null;

  // Try to get booking data from navigation state
  const stateData = location.state as BookingConfirmationState | null;

  // Priority 1: Use booking from state (fast, no API call)
  // Priority 2: Fetch booking by ID if not in state (page refresh scenario)
  const {
    data: fetchedBooking,
    isLoading: isLoadingBooking,
    error: bookingError,
  } = useBookingQuery({
    bookingId: bookingIdNum,
    // Only fetch once authenticated — the endpoint 401s for guests. When auth flips
    // true post-login, React Query auto-enables and refetches.
    enabled: isAuthenticated && !stateData?.booking && bookingIdNum !== null,
  });

  // Use state booking if available, otherwise use fetched booking
  const booking: ApiBookingWithRelations | null | undefined =
    (stateData?.booking as ApiBookingWithRelations) || fetchedBooking;

  // The API returns a booking only to its booker or a participant, so whoever sees it
  // here may — a teammate who scans the bay's QR included (deep-links D19). Total Paid
  // is the booker's amount (`paymentAmount`), not the viewer's, so it is theirs alone.
  const isBooker = Boolean(booking && user && booking.userId === safeToNumericUserId(user.id));

  // Door PIN: the createBooking response carries it on `accessControl` (state path); a hard
  // refresh re-fetches the booking, whose `accessCodeValue` the access-control service wrote.
  const accessCode: string | null | undefined =
    stateData?.accessControl?.accessCode ?? booking?.accessCodeValue;
  // The Access Codes card's block: the create response's on the state path, the booking read's
  // on a refresh. The door code falls back to the access-control result for a flow without one.
  const access = stateData?.access ?? booking?.access ?? null;
  const doorCode = access ? access.doorCode : (accessCode ?? null);

  // The club's SimLock switch — the extend offer shows only while it is on (the Bay PIN's rule
  // is the API's, in the `access` block).
  const companionEnabled = useSimulatorCompanionEnabled();

  // Opens the global SignInModal rendered by MainLayout (same hash key).
  const signInModal = useHashModal("dialog-sign-in");

  // Extract payment method ID from state or booking
  const paymentMethodIdFromState = stateData?.paymentMethodId
    ? parseInt(stateData.paymentMethodId, 10)
    : null;

  // TODO: Backend should return paymentMethodId in booking response
  // For now, we'll use the one from state if available
  const paymentMethodId = paymentMethodIdFromState;

  // Fetch payment method details to display last 4 digits
  const { data: paymentMethod, isLoading: isLoadingPaymentMethod } =
    usePaymentMethodQuery({
      userId: safeToNumericUserId(user?.id),
      paymentMethodId: paymentMethodId,
      enabled: isAuthenticated && paymentMethodId !== null,
    });

  // The booking's invoice itemizes what Total Paid does not show: the processing fee, what credit
  // or a bank payment took off it (D63), and the account credit that paid part of the booking —
  // Total Paid is the card's share. Its id rides navigation state from the create response, or
  // the booker's detail read on a refresh. Whether a fee applies was fixed when it was charged,
  // so the invoice decides — not the club's fee setting today.
  const invoiceId = stateData?.invoiceId ?? booking?.invoiceId ?? null;
  const { invoice: fetchedInvoice } = useInvoiceQuery({
    invoiceId,
    enabled: isAuthenticated && invoiceId !== null,
  });
  const receiptRows = fetchedInvoice ? deriveInvoiceTotalsRows(fetchedInvoice.totals) : null;
  // A booking deferred to the club — nothing was paid, so there is no "Total Paid" to show.
  const deferredBalance = deriveDeferredBalance(booking, fetchedInvoice);

  // Lesson variant only (QA MUST-FIX) — resolve the instructor the same way
  // `LessonPaymentStep` does (pack whitelist → club roster → nav-state
  // sugar), off `booking.staffId`, so a hard refresh still shows the real
  // name instead of dropping the row. Both queries no-op on the
  // standard/demo variants — `enabled` gates them off.
  const { userLessons: lessonUserLessons, instructor: resolvedInstructor } = useLessonInstructor({
    instructorId: booking?.staffId ?? null,
    clubId: booking?.clubId,
    isAuthenticated,
    enabled: isLesson,
    sessionName: stateData?.instructorName ?? null,
  });

  // Lesson variant only — the pack this booking drew against, so the usage card can
  // show its post-booking balance (the create call invalidates `lessons.user`, so
  // `lessonUserLessons` is already the refreshed list).
  const bookingUserLessonId = isLesson ? (booking?.userLessonId ?? null) : null;
  const lessonPack =
    bookingUserLessonId != null
      ? (lessonUserLessons.find((pack) => pack.id === bookingUserLessonId) ?? null)
      : null;

  const usageRows: UsageLimit[] | null = useMemo(
    () => (lessonPack ? lessonUsageRows(lessonPack) : null),
    [lessonPack],
  );

  const isLoading =
    authLoading || isLoadingBooking || (paymentMethodId && isLoadingPaymentMethod);

  // Derive error state from booking fetch error
  const errorMessage = bookingError
    ? bookingError.message || "Failed to load booking details"
    : null;

  // Clear booking context when confirmation page loads
  // This ensures "Book Again" button starts with a fresh booking flow — the
  // promo code included: it is spent, and every success path lands here.
  useEffect(() => {
    clearAllBookingData();
    forgetPromoCode(BOOKING_PROMO_KEY);
    if (import.meta.env.DEV) {
      console.log(
        "[BookingConfirmation] Cleared booking context from sessionStorage"
      );
    }
  }, []);

  /**
   * Handle download receipt button click
   * Downloads the booking receipt PDF via bookingService
   */
  const handleDownloadReceipt = async (bookingId: number) => {
    try {
      butterToast.info("Generating receipt...");

      // Use the booking service (follows internal API pattern)
      const result = await bookingService.downloadReceipt(bookingId);

      if (!result.success) {
        console.error("[downloadReceipt] Error:", result.message);
        butterToast(result, "Couldn’t download receipt");
        return;
      }

      // Verify blob has content
      if (result.payload.size === 0) {
        console.error("[downloadReceipt] Empty PDF received");
        butterToast.error("Receipt file is empty", "Couldn’t download receipt");
        return;
      }

      // Create a download link
      const url = window.URL.createObjectURL(result.payload);
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipt-booking-${bookingId}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);

      butterToast.success("Your receipt has been downloaded.", "Receipt downloaded");
    } catch (error) {
      console.error("[downloadReceipt] Error:", error);
      // Pass the caught value through so its message reaches the user; the string
      // fallback covers a non-Error throw and an Error with an empty message,
      // which would otherwise render butterToast's generic copy. Same shape as
      // admin-portal's toastMutationError and the other catch blocks in this app.
      butterToast.error(
        error instanceof Error && error.message
          ? error
          : "Failed to download receipt. Please try again.",
        "Couldn’t download receipt",
      );
    }
  };

  if (!bookingIdNum) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Invalid Booking
          </h1>
          <p className="text-gray-600 mb-6">No booking ID provided.</p>
          {/* "/" is the booking flow; "/booking" is not a declared route and 404s. */}
          <TenantLink
            to="/"
            className="inline-flex items-center px-6 py-2 bg-theme-600 text-white rounded-full font-medium hover:bg-theme-700"
          >
            Make a New Booking
          </TenantLink>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !booking) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        data-testid="booking-confirmation-signin-required"
      >
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Sign in Required</h1>
          <p className="text-gray-600 mb-6">
            Please sign in to view your booking details.
          </p>
          <button
            onClick={signInModal.open}
            className="px-6 py-2 bg-theme-600 text-white rounded-full font-medium text-interactive:bg-theme-700"
            aria-label="Sign in to view your booking"
            data-testid="booking-confirmation-signin-btn"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          {/* "/" is the booking flow; "/booking" is not a declared route and 404s. */}
          <TenantLink
            to="/"
            className="inline-flex items-center px-6 py-2 bg-theme-600 text-white rounded-full font-medium hover:bg-theme-700"
          >
            Make a New Booking
          </TenantLink>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Booking Not Found
          </h1>
          <p className="text-gray-600 mb-6">We couldn't find this booking.</p>
          {/* "/" is the booking flow; "/booking" is not a declared route and 404s. */}
          <TenantLink
            to="/"
            className="inline-flex items-center px-6 py-2 bg-theme-600 text-white rounded-full font-medium hover:bg-theme-700"
          >
            Make a New Booking
          </TenantLink>
        </div>
      </div>
    );
  }

  // Format duration
  const formatDuration = (minutes: number): string => {
    if (minutes === 60) return "1 Hour";
    if (minutes > 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (mins === 0) return `${hours} ${hours === 1 ? "Hour" : "Hours"}`;
      return `${hours} ${hours === 1 ? "Hour" : "Hours"} ${mins} Min`;
    }
    return `${minutes} Min`;
  };

  // Calculate duration
  const startTime = asUtcDate(new Date(booking.startTime));
  const endTime = asUtcDate(new Date(booking.endTime));
  const durationMs = endTime.getTime() - startTime.getTime();
  const durationMinutes = Math.floor(durationMs / (1000 * 60));

  // Format date and time in club timezone. The shared API type carries timezone as a
  // plain string; re-brand it for the datetime helpers.
  const timezone = asTimezone(booking.club!.timezone);
  const formattedDate = formatClubDate(
    startTime,
    timezone,
    "EEEE, d MMMM, yyyy"
  );

  // Format times using datetime library utility
  const startTimeStr = formatClubTime(startTime, timezone);
  const endTimeStr = formatClubTime(endTime, timezone);

  // Same words and rule as the booking detail's package row.
  const lessonPackageRow =
    isLesson && booking.userLessonId != null
      ? (lessonCoverage(
          booking,
          Number(booking.paymentAmount) > 0 ? `$${Number(booking.paymentAmount).toFixed(2)}` : null,
        )?.rows[0] ?? null)
      : null;

  return (
    <div className={isLesson ? "mx-auto max-w-7xl" : "px-4 sm:px-6 lg:px-8 py-12"}>
      {/* Success Message */}
      <div
      className={isLesson ? undefined : "mb-8"}
      data-testid="booking-confirmation-success-message">
        {isLesson ? (
          <LessonPageHeader
            title="Your lesson is confirmed"
            adornment={
              <span aria-hidden className="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-butter-greenLight-500 text-white">
                <CheckmarkIcon className="size-6" />
              </span>
            }
          />
        ) : (
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900 sm:text-4xl">
            {isDemo ? 'Your demo is confirmed' : 'Your booking is confirmed'}
            <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
              <CheckmarkIcon className="w-5 h-5" />
            </span>
          </h1>
        )}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* LEFT: Bay Image & Details */}
        <BayImageCard
          bay={booking.bay
            ? { ...booking.bay, description: booking.bay.description ?? undefined }
            : { id: booking.bayId, name: "Golf Bay" }}
          index={booking.bayId || 0}
        />

        {/* RIGHT: Booking Confirmation Details */}
        <div className="flex flex-col gap-3">
          {/* Ticket Card */}
          <TicketCard borderColor="var(--theme-100)" cornerRadius={28}>
            {/* Top Section: Date, Time, Duration */}
            <div className="px-6 pt-4">
              {isLesson && resolvedInstructor && (
                <InfoRow size={rowSize} label="Instructor" value={resolvedInstructor.name} testId="booking-confirmed-instructor" />
              )}
              <InfoRow size={rowSize} label="Date" value={formattedDate} testId="booking-confirmed-date" />
              <InfoRow size={rowSize} label="Time" value={`${startTimeStr} - ${endTimeStr}`} testId="booking-confirmed-time" />
              <InfoRow size={rowSize}
                label="Duration"
                value={isLesson ? formatLessonHours(durationMinutes) : formatDuration(durationMinutes)}
                testId="booking-confirmed-duration"
              />
            </div>

            {/* Divider */}
            <TicketDivider punch />

            {/* Bottom Section: Booking Details. Booking ID shows on the lesson
                variant only — on the standard/demo variants, internal
                identifiers are noise to a member; support flows resolve
                bookings by name/time. */}
            <div className="px-6 pb-3">
              {isLesson && (
                <InfoRow size={rowSize}
                  label="Booking ID"
                  value={String(booking.id)}
                  copyable
                  onCopySuccess={() => butterToast.success("Copied to clipboard")}
                  onCopyError={() => butterToast.error("Failed to copy")}
                  testId="booking-confirmed-booking-id"
                />
              )}
              {lessonPackageRow && (
                <InfoRow size={rowSize}
                  label={lessonPackageRow.label}
                  value={lessonPackageRow.value}
                  testId="booking-confirmed-lesson-package"
                />
              )}
              {paymentMethod && (
                <InfoRow size={rowSize}
                  label="Payment Method"
                  value={describePaymentMethod(paymentMethod).label}
                />
              )}
              {/* Guest fee, when the booking incurred one. Like the rest of `pricing`
                  this comes from navigation state, so it is absent on a hard refresh —
                  same limitation as the payment method above. PF-2 exposed the invoice id
                  for the processing-fee rows below; a guest-fee row read off the invoice's
                  lines is still open. */}
              {stateData?.pricing?.guestFee != null && stateData.pricing.guestFee > 0 && (
                <InfoRow size={rowSize}
                  label="Guest Fee"
                  value={`$${stateData.pricing.guestFee.toFixed(2)}`}
                />
              )}
              {/* Fee → adjustment → credit → Total Paid, the order InvoiceTotals uses. */}
              {fetchedInvoice && receiptRows?.hasProcessingFee && (
                <InfoRow size={rowSize}
                  label={`Processing Fee${fetchedInvoice.totals.processingFeeRate ? ` (${formatFeeRate(fetchedInvoice.totals.processingFeeRate)})` : ""}`}
                  value={formatInvoiceMoney(fetchedInvoice.totals.processingFee)}
                  testId="booking-confirmed-processing-fee"
                />
              )}
              {fetchedInvoice && receiptRows?.hasAdjustment && (
                <InfoRow size={rowSize}
                  label="Processing Fee Adjustment"
                  value={formatInvoiceMoney(fetchedInvoice.totals.adjustmentTotal)}
                  testId="booking-confirmed-fee-adjustment"
                />
              )}
              {fetchedInvoice && receiptRows?.hasCredit && (
                <InfoRow size={rowSize}
                  label="Account Credit"
                  value={formatInvoiceMoney(-fetchedInvoice.totals.creditApplied)}
                  testId="booking-confirmed-credit"
                />
              )}
              {/* Due at Club reads the viewer's own invoice, so only whoever owes it sees it;
                  Total Paid is the booker's money, never a participant's. */}
              {deferredBalance ? (
                <InfoRow size={rowSize}
                  label="Due at Club"
                  value={formatInvoiceMoney(deferredBalance.amountDue)}
                  testId="booking-confirmed-due-at-club"
                />
              ) : isBooker && (isLesson ? Number(booking.paymentAmount) > 0 : !isDemo) && (
                <InfoRow size={rowSize}
                  label="Total Paid"
                  value={`$${Number(booking.paymentAmount).toFixed(2)}`}
                  testId="booking-confirmed-price"
                />
              )}

              {/* Access Codes (simulator-companion D77) — the door code and the Bay PIN, lessons
                  included, by the detail page's rule: the create response carries them, and a
                  hard refresh re-reads the booking, which carries them too. */}
              <AccessCodesCard
                variant="rows"
                testIdPrefix="booking-confirmed"
                doorCode={doorCode ? { code: doorCode, worksFrom: access?.doorOpensAtLabel } : null}
                bayPin={access?.bayPin ? { code: access.bayPin.code, worksFrom: access.bayPin.opensAtLabel } : null}
              />
              {/* One more block, mid-session (Simulator Companion slice 9): this page is where
                  the bay's QR lands, so the offer is here as well as on the detail page. */}
              {!isLesson && (
                <ExtendBookingRow booking={booking} enabled={companionEnabled} timezone={booking.club?.timezone} testId="booking-confirmed-extend" />
              )}

              {/* Download Receipt — only when a payment was actually made
                  (direct pay or membership overage), not fully-covered bookings.
                  A receipt is the viewer's own invoice: a participant whose team paid
                  (`viewerPaidAmount` null) has none, so the button would only fail.
                  `paymentAmount` is the card share, so a booking credit paid in full is
                  read off its invoice (fresh from checkout) or the viewer's total. */}
              {(Number(booking.paymentAmount) > 0 || (fetchedInvoice?.totals.creditApplied ?? 0) > 0 || Number(booking.viewerPaidAmount ?? 0) > 0)
                && booking.viewerPaidAmount !== null && (
                <button
                  onClick={() => handleDownloadReceipt(booking.id)}
                  className="mt-2 flex w-full items-center justify-center gap-2 py-3 text-sm font-medium text-fg-primary transition-colors hover:text-fg-tertiary focus:outline-none focus-visible:underline sm:justify-end"
                  aria-label="Download receipt"
                >
                  <DownloadIcon className="size-5" ariaHidden />
                  Download Receipt
                </button>
              )}
            </div>
          </TicketCard>

          {/* Lesson variant only — the pack's post-booking balance (thin continuous brand bar). */}
          {lessonPack && usageRows && (
            <div data-testid="lesson-confirmed-usage-card">
              <SubscriptionUsagePanel
                collapsible={false}
                rows={usageRows}
                title={lessonPackName(lessonPack)}
                compact
                barColor="var(--theme-600)"
                barVariant="continuous"
              />
            </div>
          )}

          {/* Action Buttons - Outside Ticket Card.
              Source order is the mobile order (Book Again first). From `sm` the row
              reverses so the primary action sits on the right, which one declaration
              on the container expresses without per-child `order-*`. */}
          <div className="flex flex-col gap-3 sm:flex-row-reverse">
            {/* No repeat-CTA on a demo — one demo is the product intent. */}
            {isLesson ? (
              <TenantLink
                to="/lessons/book"
                data-testid="lesson-confirmed-book-again"
                className="btn btn-primary h-12 flex-1 rounded-full text-base font-semibold md:h-16 md:text-xl"
                aria-label="Book another lesson"
              >
                Book Again
              </TenantLink>
            ) : (
              !isDemo && (
                <TenantLink
                  to="/"
                  className="flex-1 rounded-full bg-theme-600 px-6 py-3 font-medium text-white transition-colors hover:bg-theme-700 focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 text-center"
                  aria-label="Make another booking"
                >
                  Book Again
                </TenantLink>
              )
            )}
            {isLesson ? (
              <TenantLink
                to="/plans"
                data-testid="lesson-confirmed-view-lessons"
                className="btn btn-outline h-12 flex-1 rounded-full text-base font-semibold md:h-16 md:text-xl"
                aria-label="View my plans"
              >
                View My Plans
              </TenantLink>
            ) : (
              <TenantLink
                to={`/bookings/${booking.id}`}
                className="flex-1 rounded-full border border-gray-300 bg-white px-6 py-3 font-medium text-fg-primary transition-colors hover:bg-bg-secondary focus:outline-none focus:ring-2 focus:ring-theme-600 focus:ring-offset-2 text-center"
                aria-label="View my booking details"
              >
                View My Booking
              </TenantLink>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Info Row Component
 *
 * Internal component for displaying labeled information with optional copy functionality.
 */
interface InfoRowProps {
  label: string;
  value: string;
  copyable?: boolean;
  onCopySuccess?: () => void;
  onCopyError?: () => void;
  testId?: string;
  /** 'lg' is the lesson confirmation's larger ticket rows. */
  size?: 'sm' | 'lg';
}

function InfoRow({ label, value, copyable = false, onCopySuccess, onCopyError, testId, size = 'sm' }: InfoRowProps) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className={size === 'lg' ? 'text-lg font-medium text-fg-tertiary' : 'text-sm font-medium text-fg-quaternary'}>{label}</span>
      <div className="flex items-center gap-2">
        <span className={size === 'lg' ? 'text-lg font-semibold text-fg-primary' : 'text-sm font-semibold text-fg-primary'} data-testid={testId}>{value}</span>
        {copyable && (
          <CopyButton
            text={value}
            size="default"
            onCopySuccess={onCopySuccess}
            onCopyError={onCopyError}
          />
        )}
      </div>
    </div>
  );
}
